import React from "react"
import { Flex, Text } from "@capioweb/ui-components/src"
import {
	ContentAreaProperty,
	ImageUrlItem,
	QBankImage,
} from "../../@types/CapioWeb/core"
import { BooleanProperty, StringProperty } from "../../@types/EpiFoundationSpa/Property"
import {
	PageBaseProps,
	BreadcrumbItemProps,
} from "../../components/Core/pageBase"
import { BlockResolver, BreadcrumbsBlock } from "../../components"
import { EpiContext, AnchorLinkContext, UIContext } from "../../context"
import { assetBaseUrl, shouldRender } from "../../utils"
import {
	DynamicLink,
	HEADER_HEIGHT_S,
	Image,
	scale,
} from "@capioweb/ui-components/src"
import { useLocation } from "react-router-dom"
import { scrollToAnchor } from "../../utils/scrollToAnchor"
import { HeaderContext } from '../../components/Header/context/HeaderContext'

interface CapioCommonPageProps extends PageBaseProps {
	mainContentArea: ContentAreaProperty
	regionCooperationText: StringProperty
	regionCooperationImageBlock: QBankImage
	breadcrumb: Array<BreadcrumbItemProps>
	logotypeList: ImageUrlItem[]
	alternateHeaderLayout?: BooleanProperty
}

const CapioCommonPage: React.FC<CapioCommonPageProps> = (props) => {
	const { editMode } = React.useContext(EpiContext)
	const { setAlternateHeaderLayout } = React.useContext(UIContext)

	const hasRegionCooperationInfo =
		props.regionCooperationText?.value ||
		props.regionCooperationImageBlock?.media?.value

	const { hash } = useLocation()

	const id = decodeURI(hash.substring(1))

	const renderLogo = (item: ImageUrlItem) => {
		const logo = (
			<Image
				src={assetBaseUrl() + item.imageUrl}
				title={item.link && item.linkText}
				alt={item.imageAltText}
				style={{ maxHeight: "64px", float: "right" }}
			/>
		)

		return (
			<>
				<Flex mx={0} flexDirection="column" justifyContent="center">
					{item.link ? (
						<DynamicLink url={item.link} target="_blank">
							{logo}
						</DynamicLink>
					) : (
						<>{logo}</>
					)}
				</Flex>
			</>
		)
	}

	const hasLogos = props.logotypeList?.length > 0

	const el = document?.getElementById(id)

	React.useEffect(() => {
		if (el) {
			//Scroll to anchorID position if it exists
			scrollToAnchor(el)
		}
	}, [el]) //UseEffect will run again when 'el' is ready

	React.useEffect(() => {
		if (props.alternateHeaderLayout?.value)
			setAlternateHeaderLayout(props.alternateHeaderLayout.value)
	}, [])

	return (
		<Flex display="block" w={1}>
			<BreadcrumbsBlock items={props.breadcrumb} />
			{shouldRender(editMode.inEditMode, props.mainContentArea?.value) && (
				<div data-epi-edit={"mainContentArea"}>
					<AnchorLinkContext.Provider
						value={{
							activeId: id ?? null,
						}}
					>
						<BlockResolver blocks={props.mainContentArea?.value} />
					</AnchorLinkContext.Provider>
				</div>
			)}
			{hasRegionCooperationInfo && (
				<Flex as="section" pt={{ _: 2, m: 4 }}>
					<Flex
						w={1}
						backgroundColor="blue11"
						justifyContent="center"
						alignItems="center"
						py={4}
						px={3}
					>
						<Flex
							maxw={{ _: 1, l: 141 }}
							justifyContent={hasLogos ? "space-between" : "center"}
							w={1}
							flexDirection={{ _: "column", m: "row" }}
							columnGap={2}
							rowGap={{ _: hasLogos && scale.px(4), m: 2 }}
						>
							<Flex
								borderBottom={{
									_: hasLogos,
									m: false,
								}}
							>
								<Flex
									m="0 auto"
									ml={hasLogos && { _: "auto", m: 0 }}
									flexWrap={{ _: "wrap", m: "nowrap" }}
									columnGap={2}
									rowGap={2}
									h="fit-content"
									pb={{ _: hasLogos && scale.px(4), m: 0 }}
								>
									<Flex mx={{ _: "auto", m: 0 }}>
										{shouldRender(
											editMode.inEditMode,
											props.regionCooperationImageBlock
										) && (
												<Image
													src={
														assetBaseUrl() +
														props.regionCooperationImageBlock?.media?.value?.url
													}
													title={
														props.regionCooperationImageBlock?.imageTitle?.value
													}
													alt={
														props.regionCooperationImageBlock?.alternateText
															?.value
													}
													style={{ maxHeight: "64px", float: "right" }}
												/>
											)}
									</Flex>
									{shouldRender(
										editMode.inEditMode,
										props.regionCooperationText?.value
									) && (
											<Flex
												alignItems="center"
												mx={{ _: "auto", m: 0 }}
												flexWrap={{ _: "wrap", m: "nowrap" }}
											>
												<Text
													textAlign="center"
													data-epi-edit={"regionCooperationText"}
													mb={0}
												>
													{props.regionCooperationText?.value}
												</Text>
											</Flex>
										)}
								</Flex>
							</Flex>
							{hasLogos && (
								<Flex
									m="0 auto"
									mr={{ _: "auto", m: 0 }}
									flexWrap={{ _: "wrap", m: "nowrap" }}
									columnGap={2}
									rowGap={2}
									justifyContent="center"
								>
									{props.logotypeList &&
										props.logotypeList.map((item: ImageUrlItem) =>
											renderLogo(item)
										)}
								</Flex>
							)}
						</Flex>
					</Flex>
				</Flex>
			)}
		</Flex>
	)
}

export { CapioCommonPage }
